import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { X, Check, RotateCcw } from 'lucide-react'

interface BannerImageCropperProps {
  imageFile: File
  onCropComplete: (croppedImageUrl: string, cropData: CropData) => void
  onCancel: () => void
}

export interface CropData {
  centerY: number // Y center position as percentage (0-100) of original image
  originalWidth: number // Original image width in pixels
  originalHeight: number // Original image height in pixels
}

export function BannerImageCropper({ imageFile, onCropComplete, onCancel }: BannerImageCropperProps) {
  const [imageUrl, setImageUrl] = useState<string>('')
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 })
  const [displayedImageBounds, setDisplayedImageBounds] = useState({ left: 0, width: 100, top: 0, height: 100 })
  const [cropRectangleY, setCropRectangleY] = useState(25) // Y position as percentage (0-100)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  // Target aspect ratio for banner (3:1)
  const targetAspectRatio = 3

  // Calculate the correct height for the crop rectangle to maintain 3:1 aspect ratio
  const calculateCropRectangleHeight = () => {
    if (!containerRef.current || !displayedImageBounds.width) {
      return 33.33 // Default fallback
    }

    const container = containerRef.current
    const containerRect = container.getBoundingClientRect()
    
    // Calculate the actual pixel width of the displayed image
    const displayedImagePixelWidth = (displayedImageBounds.width / 100) * containerRect.width
    
    // Calculate the required height for 3:1 aspect ratio
    const requiredPixelHeight = displayedImagePixelWidth / targetAspectRatio
    
    // Convert back to percentage of the displayed image height
    const displayedImagePixelHeight = (displayedImageBounds.height / 100) * containerRect.height
    const cropHeightPercentage = (requiredPixelHeight / displayedImagePixelHeight) * 100
    
    return Math.min(cropHeightPercentage, 100) // Cap at 100%
  }

  // Calculate the actual bounds of the displayed image within the container
  const calculateDisplayedImageBounds = () => {
    if (!containerRef.current || !imageDimensions.width || !imageDimensions.height) {
      return { left: 0, width: 100, top: 0, height: 100 }
    }

    const container = containerRef.current
    const containerRect = container.getBoundingClientRect()
    const containerAspectRatio = containerRect.width / containerRect.height
    const imageAspectRatio = imageDimensions.width / imageDimensions.height

    let displayedImageBounds = { left: 0, width: 100, top: 0, height: 100 }

    if (imageAspectRatio > containerAspectRatio) {
      // Image is wider than container - letterboxed top/bottom
      const displayedHeight = (containerAspectRatio / imageAspectRatio) * 100
      displayedImageBounds = {
        left: 0,
        width: 100,
        top: (100 - displayedHeight) / 2,
        height: displayedHeight
      }
    } else {
      // Image is taller than container - letterboxed left/right
      const displayedWidth = (imageAspectRatio / containerAspectRatio) * 100
      displayedImageBounds = {
        left: (100 - displayedWidth) / 2,
        width: displayedWidth,
        top: 0,
        height: 100
      }
    }

    return displayedImageBounds
  }

  useEffect(() => {
    // Create image URL and load image to get dimensions
    const url = URL.createObjectURL(imageFile)
    setImageUrl(url)

    const img = new Image()
    img.onload = () => {
      setImageDimensions({ width: img.width, height: img.height })
      
      // Start with crop rectangle centered vertically
      setCropRectangleY(25) // Start at 25% from top
    }
    img.src = url

    return () => URL.revokeObjectURL(url)
  }, [imageFile])

  // Update displayed image bounds when dimensions change
  useEffect(() => {
    if (imageDimensions.width && imageDimensions.height) {
      const bounds = calculateDisplayedImageBounds()
      setDisplayedImageBounds(bounds)
    }
  }, [imageDimensions])

  // Recalculate bounds on window resize
  useEffect(() => {
    const handleResize = () => {
      if (imageDimensions.width && imageDimensions.height) {
        const bounds = calculateDisplayedImageBounds()
        setDisplayedImageBounds(bounds)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [imageDimensions])

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    setDragStart({
      y: e.clientY
    })
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return

    const container = containerRef.current
    const containerRect = container.getBoundingClientRect()
    
    const deltaY = ((e.clientY - dragStart.y) / containerRect.height) * 100

    setCropRectangleY(prev => {
      const cropHeight = calculateCropRectangleHeight()
      const newY = Math.max(0, Math.min(100 - cropHeight, prev + deltaY))
      return newY
    })

    setDragStart({
      y: e.clientY
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleCropComplete = () => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Calculate the center Y position of the crop rectangle in pixels
    const cropRectHeight = calculateCropRectangleHeight()
    const cropCenterY = ((cropRectangleY + cropRectHeight / 2) / 100) * imageDimensions.height
    
    // Calculate crop dimensions to maintain 3:1 aspect ratio
    const cropWidth = imageDimensions.width
    const cropHeight = cropWidth / targetAspectRatio
    
    // Calculate crop Y position to center on the selected Y coordinate
    let cropY = cropCenterY - cropHeight / 2
    
    // Ensure crop doesn't go outside image bounds
    cropY = Math.max(0, Math.min(imageDimensions.height - cropHeight, cropY))
    
    // Set canvas size with reasonable max dimensions
    const maxWidth = 1200
    canvas.width = Math.min(maxWidth, cropWidth)
    canvas.height = canvas.width / targetAspectRatio

    // Create image and draw cropped portion
    const img = new Image()
    img.onload = () => {
      ctx.drawImage(
        img,
        0, cropY, cropWidth, cropHeight, // Source rectangle
        0, 0, canvas.width, canvas.height // Destination rectangle
      )

      // Convert to blob and create URL
      canvas.toBlob((blob) => {
        if (blob) {
          const croppedUrl = URL.createObjectURL(blob)
          const cropData: CropData = {
            centerY: (cropCenterY / imageDimensions.height) * 100,
            originalWidth: imageDimensions.width,
            originalHeight: imageDimensions.height
          }
          onCropComplete(croppedUrl, cropData)
        }
      }, 'image/jpeg', 0.9)
    }
    img.src = imageUrl
  }

  const resetCrop = () => {
    // Reset to center position
    const cropHeight = calculateCropRectangleHeight()
    setCropRectangleY(50 - cropHeight / 2)
  }

  return (
    <Card className="w-full max-w-[calc(700px+1.5rem)] mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Crop Banner Image</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={resetCrop}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button variant="outline" size="sm" onClick={onCancel}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button size="sm" onClick={handleCropComplete}>
              <Check className="w-4 h-4 mr-2" />
              Use This Crop
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Drag the crop rectangle up or down to choose the vertical center for your banner. The red dashed line shows the image center.
          </p>
          
          <div 
            ref={containerRef}
            className="relative w-full max-w-3xl mx-auto rounded-lg overflow-hidden cursor-ns-resize"
            style={{ 
              aspectRatio: imageDimensions.width && imageDimensions.height ? `${imageDimensions.width}/${imageDimensions.height}` : '16/9',
              backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              minHeight: '300px',
              maxHeight: '60vh'
            }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {/* Crop Selection Rectangle */}
            <div
              className="absolute border-2 border-white cursor-ns-resize z-10"
              style={{
                left: `${displayedImageBounds.left}%`,
                top: `${displayedImageBounds.top + (cropRectangleY * displayedImageBounds.height / 100)}%`,
                width: `${displayedImageBounds.width}%`,
                height: `${calculateCropRectangleHeight() * displayedImageBounds.height / 100}%`,
              }}
              onMouseDown={handleMouseDown}
            >
              {/* Center line of crop rectangle */}
              <div 
                className="absolute top-1/2 left-0 right-0 h-0.5 border-t-2 border-yellow-400 border-dashed transform -translate-y-1/2 pointer-events-none"
              />
              
              {/* Drag handles on sides */}
              <div className="absolute top-1/2 -left-2 w-4 h-8 bg-white border border-gray-400 rounded transform -translate-y-1/2 cursor-ns-resize" />
              <div className="absolute top-1/2 -right-2 w-4 h-8 bg-white border border-gray-400 rounded transform -translate-y-1/2 cursor-ns-resize" />
              
              {/* Center indicator */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-yellow-400 rounded-full border-2 border-white" />
            </div>
          </div>
          
          <div className="text-center text-sm text-muted-foreground">
            <p>Target ratio: 3:1 (landscape) • Crop center: {(cropRectangleY + calculateCropRectangleHeight() / 2).toFixed(1)}% from top</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
